import React, { useState, useEffect } from 'react';
import { Box, Button, Flex, Text, IconButton, TextField } from '@radix-ui/themes';
import Icon from '@mdi/react';
import { useStore } from '../../store/store';
import avatar from '../../assets/avatar2.png';
import { mdiPlus, mdiCheck, mdiChevronLeft, mdiChevronRight, mdiClose, mdiArrowUp, mdiArrowDown, mdiLightningBolt } from '@mdi/js';

const CreateNonFictionTopics = ({ 
  novelId, 
  selectedChapters,
  selectedTopics,
  setSelectedTopics,
  generatedSets,
  currentSetIndex,
  onGenerateTopics,
  onNavigateSet
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [customTopic, setCustomTopic] = useState('');
  const [hoverIndex, setHoverIndex] = useState(null);
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const [state, actions] = useStore();

  // Initialize topics for each chapter using chapter IDs
  useEffect(() => {
    const initialTopics = {};
    selectedChapters.forEach((chapter) => {
      if (!selectedTopics[chapter.id]) {
        initialTopics[chapter.id] = [];
      }
    });
    
    if (Object.keys(initialTopics).length > 0) {
      setSelectedTopics(prev => ({ ...prev, ...initialTopics }));
    }
  }, [selectedChapters]);

  const currentChapter = selectedChapters[currentChapterIndex] || {};

  const handleGenerateTopics = async () => {
    if (!currentChapter?.title) {
      setError("No chapter selected");
      return;
    }

    setLoading(true);
    setError(null);
    
    const result = await onGenerateTopics(currentChapter.title, currentChapter.id);
    
    if (result.error) {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleAddTopic = (topic) => {
    const chapterId = currentChapter.id;
    const currentTopics = selectedTopics[chapterId] || [];
    if (!currentTopics.includes(topic)) {
      setSelectedTopics({
        ...selectedTopics,
        [chapterId]: [...currentTopics, topic]
      });
    }
  };

  const handleRemoveTopic = (index) => {
    const chapterId = currentChapter.id;
    const currentTopics = [...(selectedTopics[chapterId] || [])];
    currentTopics.splice(index, 1);
    setSelectedTopics({
      ...selectedTopics,
      [chapterId]: currentTopics
    });
  };

  const handleMoveUp = (index) => {
    if (index <= 0) return; // Can't move up the first item
    
    const chapterId = currentChapter.id;
    const currentTopics = [...(selectedTopics[chapterId] || [])];
    [currentTopics[index-1], currentTopics[index]] = 
    [currentTopics[index], currentTopics[index-1]];
    
    setSelectedTopics({
      ...selectedTopics,
      [chapterId]: currentTopics
    });
  };
  
  const handleMoveDown = (index) => {
    const chapterId = currentChapter.id;
    const currentTopics = selectedTopics[chapterId] || [];
    if (index >= currentTopics.length - 1) return; // Can't move down the last item
    
    const newTopics = [...currentTopics];
    [newTopics[index], newTopics[index+1]] = 
    [newTopics[index+1], newTopics[index]];
    
    setSelectedTopics({
      ...selectedTopics,
      [chapterId]: newTopics
    });
  };

  const navigateToSet = (direction) => {
    const chapterId = currentChapter.id;
    onNavigateSet(chapterId, direction);
  };

  const handleAddCustomTopic = () => {
    if (!customTopic.trim()) return;
    
    const chapterId = currentChapter.id;
    const currentTopics = selectedTopics[chapterId] || [];
    if (!currentTopics.includes(customTopic.trim())) {
      setSelectedTopics({
        ...selectedTopics,
        [chapterId]: [...currentTopics, customTopic.trim()]
      });
      setCustomTopic(''); // Clear the input after adding
    }
  };

  const handleCustomTopicKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleAddCustomTopic();
    }
  };

  const navigateToChapter = (direction) => {
    const newIndex = currentChapterIndex + direction;
    if (newIndex >= 0 && newIndex < selectedChapters.length) {
      setCurrentChapterIndex(newIndex);
    }
  };

  // Add this new function to handle adding all topics
  const handleAddAllTopics = () => {
    if (!currentTopics.length) return;
    
    const chapterId = currentChapter.id;
    const currentSelectedList = selectedTopics[chapterId] || [];
    
    // Filter out topics that are already in the selected list
    const newTopics = currentTopics.filter(topic => !currentSelectedList.includes(topic));
    
    if (newTopics.length > 0) {
      setSelectedTopics({
        ...selectedTopics,
        [chapterId]: [...currentSelectedList, ...newTopics]
      });
    }
  };

  // Get current topics for the active chapter and set
  const chapterId = currentChapter?.id;
  const chapterSets = chapterId ? (generatedSets[chapterId] || []) : [];
  const activeSetIndex = chapterId ? (currentSetIndex[chapterId] || 0) : 0;
  const currentTopics = chapterSets.length > 0 && activeSetIndex < chapterSets.length ? 
    chapterSets[activeSetIndex] : [];
  
  // Get selected topics for the current chapter
  const currentSelectedTopics = chapterId ? (selectedTopics[chapterId] || []) : [];

  return (
    <>
      {state.novels.length === 0 ?
      <Flex className="tutorial-container" style={{ }}>
          <Flex className={"tutorial-text"}>
            <img src={avatar} alt="Avatar" className="tutorial-avatar" />
            <div>This works similiar to the last step, but here we're generating a list of topics that should be discuessed in the chapter. You can generate topics using AI, or add you own ones on the right hand side. We have to to this for each chapter, which you can navigate through using the arrow buttons on the top. Once you're done, hit the 'Finish' button!</div>
          </Flex>
      </Flex> : null}

      <Flex justify="between" align="center" mb="4" className="custom-chapter-navigation">
        <Button 
          className="custom-nav-button"
          disabled={currentChapterIndex === 0}
          onClick={() => navigateToChapter(-1)}
        >
          <Icon path={mdiChevronLeft} size={0.8} />
          <span>Previous Chapter</span>
        </Button>
        
        <Text className="custom-chapter-badge">
          Chapter {currentChapterIndex + 1} of {selectedChapters.length}
        </Text>
        
        <Button 
          className="custom-nav-button"
          disabled={currentChapterIndex >= selectedChapters.length - 1}
          onClick={() => navigateToChapter(1)}
        >
          <span>Next Chapter</span>
          <Icon path={mdiChevronRight} size={0.8} />
        </Button>
      </Flex>

      <Box className="custom-topic-card">
        <Box className="custom-topic-info">
          <Text size="1" className="custom-label">Current Chapter</Text>
          <Text size="3" className="custom-topic-text">{currentChapter?.title || 'No chapter selected'}</Text>
        </Box>
      </Box>
      <div className="nonfiction-container">
        {/* Left column - Generation and display of topic sets */}
        <Flex direction="column" className="nonfiction-column custom-column">
          <Box className="nonfiction-content custom-content">
            <Flex justify="between" align="center" className="nonfiction-header custom-header">
              <Text className="nonfiction-title custom-title" as="h3">Generated Topics</Text>
              <Flex gap="2">
                <Button 
                  className="custom-button"
                  onClick={handleAddAllTopics}
                  disabled={currentTopics.length === 0}
                >
                  <Flex align="center" gap="2">
                    <Icon path={mdiPlus} size={0.8} />
                    <span>Add All</span>
                  </Flex>
                </Button>
                <Button 
                  className="custom-button"
                  onClick={handleGenerateTopics}
                  disabled={loading || !currentChapter.title}
                >
                  <Flex align="center" gap="2">
                    {loading ? (
                      <div className="custom-spinner" />
                    ) : (
                      <Icon path={mdiLightningBolt} size={0.8} />
                    )}
                    <span>{chapterSets.length === 0 ? "Generate Topics" : "Generate New Set"}</span>
                  </Flex>
                </Button>
              </Flex>
            </Flex>
            
            {error && (
              <Box className="custom-error-message">
                <Icon path={mdiClose} size={0.7} />
                <Text className="custom-error-text">{error}</Text>
              </Box>
            )}

            <Box className="custom-chapters-card">
              {currentTopics.length > 0 && (
                <>
                  <Box className="custom-set-navigation">
                    {chapterSets.length > 1 && (
                      <Flex justify="between" align="center" className="custom-set-controls">
                        <Button 
                          variant="outline" 
                          className="custom-nav-button"
                          disabled={activeSetIndex <= 0}
                          onClick={() => navigateToSet(-1)}
                        >
                          <Icon path={mdiChevronLeft} size={0.8} />
                          <span>Previous Set</span>
                        </Button>
                        
                        <Text className="custom-set-badge">
                          Set {activeSetIndex + 1} of {chapterSets.length}
                        </Text>
                        
                        <Button 
                          variant="outline"
                          className="custom-nav-button"
                          disabled={activeSetIndex >= chapterSets.length - 1}
                          onClick={() => navigateToSet(1)}
                        >
                          <span>Next Set</span>
                          <Icon path={mdiChevronRight} size={0.8} />
                        </Button>
                      </Flex>
                    )}
                  </Box>
                  
                  <Box className="custom-chapter-list">
                    {currentTopics.map((topic, index) => (
                      <div
                        key={`generated-${index}`} 
                        className="custom-chapter-item"
                        onMouseEnter={() => setHoverIndex(`left-${index}`)}
                        onMouseLeave={() => setHoverIndex(null)}
                      >
                        <Flex justify="between" align="center" style={{height: '35px'}}>
                          <Text as={"div"} className="custom-chapter-text">
                            <div className="custom-chapter-number">{index + 1}. {topic}</div>
                          </Text>
                        </Flex>
                        {(hoverIndex === `left-${index}` || currentSelectedTopics.includes(topic)) && (
                          <IconButton 
                            style={{marginTop: '-33px'}}
                            className={`custom-add-button ${currentSelectedTopics.includes(topic) ? 'selected' : ''}`}
                            onClick={() => handleAddTopic(topic)}
                          >
                            <Icon path={currentSelectedTopics.includes(topic) ? mdiCheck : mdiPlus} size={0.8} />
                          </IconButton>
                        )}
                      </div>
                    ))}
                  </Box>
                </>
              )}

              {chapterSets.length === 0 && !loading && (
                <Flex direction="column" align="center" justify="center" className="custom-empty-placeholder">
                  <Icon path={mdiLightningBolt} size={1.5} className="custom-empty-icon" />
                  <Text className="custom-empty-text">Click the Generate button to create topics for this chapter</Text>
                </Flex>
              )}
            </Box>
          </Box>
        </Flex>

        {/* Right column - Selected topics */}
        <Flex direction="column" className="nonfiction-column custom-column">
          <Box className="nonfiction-content custom-content">
            <div class='custom-header'>
            <Text className="nonfiction-title custom-title" as="h3">Selected Topics for this Chapter</Text>
            </div>

            <Box className="custom-selection-card">
              {currentSelectedTopics.length === 0 ? (
                <>
                <div>
                    <Flex className="custom-add-chapter-form" align="center" gap="2">
                        <div className="custom-add-chapter-input" size="2" style={{ flexGrow: 1 }}>
                            <TextField.Root 
                            placeholder="Enter custom topic..." 
                            value={customTopic} 
                            onChange={(e) => setCustomTopic(e.target.value)}
                            onKeyDown={handleCustomTopicKeyDown}
                            />
                        </div>
                        <Button 
                            className="custom-add-chapter-button"
                            disabled={!customTopic.trim()}
                            onClick={handleAddCustomTopic}
                        >
                            Add Custom
                        </Button>
                    </Flex>
                </div>
                <Flex direction="column" align="center" justify="center" className="custom-empty-selection">
                  <Icon path={mdiChevronLeft} size={1.5} className="custom-empty-icon" />
                  <Text className="custom-empty-text">Select topics from the generated list or add custom topics</Text>
                </Flex>
                </>
              ) : (
                <>
                  {/* Add custom topic input field */}
                    <Flex className="custom-add-chapter-form" align="center" gap="2">
                        <div className="custom-add-chapter-input" size="2" style={{ flexGrow: 1 }}>
                            <TextField.Root 
                            placeholder="Enter custom topic..." 
                            value={customTopic} 
                            onChange={(e) => setCustomTopic(e.target.value)}
                            onKeyDown={handleCustomTopicKeyDown}
                            />
                        </div>
                        <Button 
                            className="custom-add-chapter-button"
                            disabled={!customTopic.trim()}
                            onClick={handleAddCustomTopic}
                        >
                            Add Custom
                        </Button>
                    </Flex>
                  
                  <Box className="custom-selection-list">
                    {currentSelectedTopics.map((topic, index) => (
                      <div 
                        key={`selected-${index}`} 
                        className="custom-selection-item"
                        onMouseEnter={() => setHoverIndex(`right-${index}`)}
                        onMouseLeave={() => setHoverIndex(null)}
                      >
                        <Flex justify="between" align="center">
                          <Text className="custom-selection-text">
                            <span className="custom-selection-number">{index + 1}.</span> 
                            {topic}
                          </Text>
                        </Flex>
                        {hoverIndex === `right-${index}` && (
                            <Flex className="custom-selection-actions">
                              <IconButton 
                                className="custom-action-button"
                                disabled={index === 0}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMoveUp(index);
                                }}
                              >
                                <Icon path={mdiArrowUp} size={0.7} />
                              </IconButton>
                              
                              <IconButton 
                                className="custom-action-button"
                                disabled={index === currentSelectedTopics.length - 1}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMoveDown(index);
                                }}
                              >
                                <Icon path={mdiArrowDown} size={0.7} />
                              </IconButton>
                              
                              <IconButton 
                                className="custom-remove-button"
                                onClick={() => handleRemoveTopic(index)}
                              >
                                <Icon path={mdiClose} size={0.7} />
                              </IconButton>
                            </Flex>
                          )}
                      </div>
                    ))}
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Flex>
      </div>
    </>
  );
};

export default CreateNonFictionTopics;
